/**
 * API client for the collection caching server
 * Provides filtered token queries with pagination
 */

import { CollectionToken, TraitSummary, PaginatedResponse } from './types';
import { getApiUrl } from './config';

const API_BASE = '/api/collections';

/**
 * Helper to build full API URL
 */
function apiUrl(path: string): string {
  return getApiUrl(`${API_BASE}${path}`);
}

export interface CollectionStatus {
  indexed: boolean;
  tokenCount?: number;
  builtAt?: string;
  expiresAt?: string;
  isStale?: boolean;
  ageMinutes?: number;
}

export interface TraitsResponse {
  traits: TraitSummary[];
  totalSupply: number;
  status: {
    builtAt: string;
    expiresAt: string;
    tokenCount: string;
  };
}

export interface TokensResponse {
  items: CollectionToken[];
  pagination: {
    offset: number;
    limit: number;
    hasMore: boolean;
    totalCount: number;
  };
}

/**
 * Fetch collection status (is it indexed, how old is the cache)
 */
export async function fetchCollectionStatus(address: string): Promise<CollectionStatus> {
  const response = await fetch(apiUrl(`/${address}/status`));
  if (!response.ok) {
    throw new Error(`Failed to fetch collection status: ${response.status}`);
  }
  return response.json();
}

/**
 * Fetch traits for a collection (for filter UI)
 */
export async function fetchCollectionTraits(address: string): Promise<TraitsResponse> {
  const response = await fetch(apiUrl(`/${address}/traits`));
  if (!response.ok) {
    throw new Error(`Failed to fetch traits: ${response.status}`);
  }
  return response.json();
}

// Sort options available for collections
export type SortOption =
  | 'price_asc'
  | 'price_desc'
  | 'tokenId_asc'
  | 'tokenId_desc'
  | 'rarity_asc'
  | 'rarity_desc'
  | 'created_asc'
  | 'created_desc'
  | 'transferred_asc'
  | 'transferred_desc'
  | 'favorites_asc'
  | 'favorites_desc';

export const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'price_asc', label: 'Price (Low to High)' },
  { value: 'price_desc', label: 'Price (High to Low)' },
  { value: 'tokenId_asc', label: 'Token ID (Low to High)' },
  { value: 'tokenId_desc', label: 'Token ID (High to Low)' },
  { value: 'favorites_desc', label: 'Most Favorited' },
  { value: 'favorites_asc', label: 'Least Favorited' },
  { value: 'rarity_desc', label: 'Rarity (Rarest First)' },
  { value: 'rarity_asc', label: 'Rarity (Common First)' },
  { value: 'created_desc', label: 'Recently Created' },
  { value: 'created_asc', label: 'Oldest Created' },
  { value: 'transferred_desc', label: 'Recently Transferred' },
  { value: 'transferred_asc', label: 'Least Recently Transferred' },
];

/**
 * Fetch tokens with optional filtering and pagination
 */
export async function fetchFilteredTokens(
  address: string,
  options: {
    offset?: number;
    limit?: number;
    sort?: SortOption;
    traits?: Record<string, string[]>;
  } = {}
): Promise<PaginatedResponse<CollectionToken>> {
  const { offset = 0, limit = 24, sort = 'price_asc', traits = {} } = options;

  // Build query params
  const params = new URLSearchParams();
  params.set('offset', String(offset));
  params.set('limit', String(limit));
  params.set('sort', sort);

  // Add trait filters: trait.Hat=🎧,💀&trait.Body=🏀
  for (const [traitName, values] of Object.entries(traits)) {
    if (values.length > 0) {
      params.set(`trait.${traitName}`, values.join(','));
    }
  }

  const url = apiUrl(`/${address}/tokens?${params.toString()}`);
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch tokens: ${response.status}`);
  }

  const data: TokensResponse = await response.json();

  return {
    items: data.items,
    pagination: {
      offset: data.pagination.offset,
      limit: data.pagination.limit,
      hasMore: data.pagination.hasMore,
      totalCount: data.pagination.totalCount,
    },
  };
}

/**
 * Trigger a cache refresh for a collection
 */
export async function refreshCollectionCache(address: string): Promise<{ ok: boolean; total: number }> {
  const response = await fetch(apiUrl(`/${address}/refresh`), {
    method: 'POST',
  });
  if (!response.ok) {
    throw new Error(`Failed to refresh cache: ${response.status}`);
  }
  return response.json();
}

/**
 * Emoji with duplicate counts
 */
export interface DuplicateEmojiInfo {
  emoji: string;
  doublesCount: number;
  triplesCount: number;
  doubleDoublesCount: number;
}

/**
 * Response from duplicate emoji endpoint
 */
export interface DuplicatesResponse {
  items: Array<CollectionToken & { duplicateEmojis: Array<{ emoji: string; count: number }> }>;
  pagination: {
    offset: number;
    limit: number;
    hasMore: boolean;
    totalCount: number;
  };
  availableEmojis: DuplicateEmojiInfo[];
  minCount: number;
  minTypes: number;
  filterEmojis: string[];
  doubleDoublesCount: number; // Tokens with 2+ different duplicate emojis
}

/**
 * Fetch tokens with duplicate emojis (same emoji in multiple trait slots)
 */
export async function fetchDuplicateEmojis(
  address: string,
  options: {
    minCount?: number; // 2 = doubles, 3 = triples
    minTypes?: number; // minimum different duplicate emoji types (2 = "double double")
    emojis?: string[]; // filter by specific emojis (OR logic)
    offset?: number;
    limit?: number;
  } = {}
): Promise<DuplicatesResponse> {
  const { minCount = 2, minTypes = 1, emojis = [], offset = 0, limit = 24 } = options;

  const params = new URLSearchParams();
  params.set('minCount', String(minCount));
  params.set('minTypes', String(minTypes));
  params.set('offset', String(offset));
  params.set('limit', String(limit));
  if (emojis.length > 0) {
    params.set('emojis', emojis.join(','));
  }

  const url = apiUrl(`/${address}/duplicates?${params.toString()}`);
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch duplicates: ${response.status}`);
  }

  return response.json();
}

/**
 * Check if the API server is available
 */
export async function isApiAvailable(): Promise<boolean> {
  try {
    const response = await fetch(getApiUrl('/health'), { method: 'GET' });
    return response.ok;
  } catch {
    return false;
  }
}
